import React, { useEffect, useMemo, useState } from "react";
import { ModalHeader, ModalShell } from "components/ui/ModalFrame";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Button from "components/ui/Button";
import { MODAL_LAYER } from "constants/modalLayers";
import { estadoCivilPessoaFisicaOptions } from "documents/dynamicTextRegistry";
import { MODAL_STICKY_FOOTER_END } from "constants/layoutSpacing";
import { cpfDigitsOnly, maskCpf, phoneDigitsOnly, maskPhoneBr } from "utils/masks";

const emptyConjuge = () => ({
  NOME_CONJUGE: "",
  CPF_CONJUGE: "",
  RG_CONJUGE: "",
  NACIONALIDADE_CONJUGE: "",
  PROFISSAO_CONJUGE: "",
  EMAIL_CONJUGE: "",
});

export const emptyCustomerFormState = () => ({
  NOME_PESSOA_FISICA: "",
  CPF_PESSOA_FISICA: "",
  RG_PESSOA_FISICA: "",
  NACIONALIDADE_PESSOA_FISICA: "",
  PROFISSAO_PESSOA_FISICA: "",
  ESTADO_CIVIL_PESSOA_FISICA: "",
  RUA_ENDERECO_PESSOA_FISICA: "",
  N_ENDERECO_PESSOA_FISICA: "",
  BAIRRO_ENDERECO_PESSOA_FISICA: "",
  MUN_ENDERECO_PESSOA_FISICA: "",
  CEP_PESSOA_FISICA: "",
  EMAIL_PESSOA_FISICA: "",
  TEL_PESSOA_FISICA: "",
  conjuge: emptyConjuge(),
});

export const requiresConjugeFields = (estadoCivil) => estadoCivil === "casado";

/** Maps API / nested payload into flat form state + conjuge */
export function mapPayloadToFormState(raw) {
  if (!raw || typeof raw !== "object") return emptyCustomerFormState();

  const conj =
    raw.conjuge && typeof raw.conjuge === "object"
      ? { ...emptyConjuge(), ...raw.conjuge }
      : emptyConjuge();

  const base = emptyCustomerFormState();
  return {
    ...base,
    NOME_PESSOA_FISICA: raw.NOME_PESSOA_FISICA ?? "",
    CPF_PESSOA_FISICA: cpfDigitsOnly(raw.CPF_PESSOA_FISICA ?? ""),
    RG_PESSOA_FISICA: raw.RG_PESSOA_FISICA ?? "",
    NACIONALIDADE_PESSOA_FISICA: raw.NACIONALIDADE_PESSOA_FISICA ?? "",
    PROFISSAO_PESSOA_FISICA: raw.PROFISSAO_PESSOA_FISICA ?? "",
    ESTADO_CIVIL_PESSOA_FISICA: raw.ESTADO_CIVIL_PESSOA_FISICA ?? "",
    RUA_ENDERECO_PESSOA_FISICA: raw.RUA_ENDERECO_PESSOA_FISICA ?? "",
    N_ENDERECO_PESSOA_FISICA: raw.N_ENDERECO_PESSOA_FISICA ?? "",
    BAIRRO_ENDERECO_PESSOA_FISICA: raw.BAIRRO_ENDERECO_PESSOA_FISICA ?? "",
    MUN_ENDERECO_PESSOA_FISICA: raw.MUN_ENDERECO_PESSOA_FISICA ?? "",
    CEP_PESSOA_FISICA: raw.CEP_PESSOA_FISICA ?? "",
    EMAIL_PESSOA_FISICA: raw.EMAIL_PESSOA_FISICA ?? "",
    TEL_PESSOA_FISICA: phoneDigitsOnly(raw.TEL_PESSOA_FISICA ?? ""),
    conjuge: {
      ...conj,
      CPF_CONJUGE: cpfDigitsOnly(conj.CPF_CONJUGE ?? ""),
    },
  };
}

function buildSavePayload(form, { id, isEdit }) {
  const estado = form.ESTADO_CIVIL_PESSOA_FISICA;
  const showConjuge = requiresConjugeFields(estado);

  const body = {
    NOME_PESSOA_FISICA: form.NOME_PESSOA_FISICA.trim(),
    CPF_PESSOA_FISICA: cpfDigitsOnly(form.CPF_PESSOA_FISICA),
    RG_PESSOA_FISICA: form.RG_PESSOA_FISICA.trim(),
    NACIONALIDADE_PESSOA_FISICA: form.NACIONALIDADE_PESSOA_FISICA.trim(),
    PROFISSAO_PESSOA_FISICA: form.PROFISSAO_PESSOA_FISICA.trim(),
    ESTADO_CIVIL_PESSOA_FISICA: estado,
    RUA_ENDERECO_PESSOA_FISICA: form.RUA_ENDERECO_PESSOA_FISICA.trim(),
    N_ENDERECO_PESSOA_FISICA: form.N_ENDERECO_PESSOA_FISICA.trim(),
    BAIRRO_ENDERECO_PESSOA_FISICA: form.BAIRRO_ENDERECO_PESSOA_FISICA.trim(),
    MUN_ENDERECO_PESSOA_FISICA: form.MUN_ENDERECO_PESSOA_FISICA.trim(),
    CEP_PESSOA_FISICA: form.CEP_PESSOA_FISICA.trim(),
    EMAIL_PESSOA_FISICA: form.EMAIL_PESSOA_FISICA.trim(),
    TEL_PESSOA_FISICA: phoneDigitsOnly(form.TEL_PESSOA_FISICA),
  };

  if (isEdit && id) {
    body.id = id;
  } else {
    body.id = crypto.randomUUID();
  }

  if (showConjuge) {
    body.conjuge = {
      NOME_CONJUGE: form.conjuge.NOME_CONJUGE.trim(),
      CPF_CONJUGE: cpfDigitsOnly(form.conjuge.CPF_CONJUGE),
      RG_CONJUGE: form.conjuge.RG_CONJUGE.trim(),
      NACIONALIDADE_CONJUGE: form.conjuge.NACIONALIDADE_CONJUGE.trim(),
      PROFISSAO_CONJUGE: form.conjuge.PROFISSAO_CONJUGE.trim(),
      EMAIL_CONJUGE: form.conjuge.EMAIL_CONJUGE.trim(),
    };
  } else {
    body.conjuge = {};
  }

  return body;
}

const CustomerFormModal = ({
  isOpen,
  mode,
  customerId,
  initialFormState,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState(emptyCustomerFormState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setForm(initialFormState ? mapPayloadToFormState(initialFormState) : emptyCustomerFormState());
  }, [isOpen, initialFormState, mode, customerId]);

  const showConjuge = useMemo(
    () => requiresConjugeFields(form.ESTADO_CIVIL_PESSOA_FISICA),
    [form.ESTADO_CIVIL_PESSOA_FISICA]
  );

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateConjuge = (key, value) => {
    setForm((prev) => ({
      ...prev,
      conjuge: { ...prev.conjuge, [key]: value },
    }));
  };

  const handleEstadoCivilChange = (value) => {
    setForm((prev) => {
      const next = { ...prev, ESTADO_CIVIL_PESSOA_FISICA: value };
      if (!requiresConjugeFields(value)) {
        next.conjuge = emptyConjuge();
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.NOME_PESSOA_FISICA.trim() || !cpfDigitsOnly(form.CPF_PESSOA_FISICA)) {
      setError("Nome e CPF são obrigatórios.");
      return;
    }

    if (!form.ESTADO_CIVIL_PESSOA_FISICA) {
      setError("Selecione o estado civil.");
      return;
    }

    if (showConjuge && !form.conjuge.NOME_CONJUGE.trim()) {
      setError("Informe o nome do cônjuge.");
      return;
    }

    setSaving(true);
    try {
      const payload = buildSavePayload(form, {
        id: customerId,
        isEdit,
      });
      await onSaved(payload);
      onClose();
    } catch (err) {
      setError(err?.message || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      zClass={MODAL_LAYER.standard}
      backdropClassName="bg-black/50"
      panelClassName="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-elevation-lg flex flex-col"
    >
      <ModalHeader
        title={isEdit ? "Editar cliente" : "Adicionar cliente"}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <p className="text-sm text-destructive font-medium" role="alert">
              {error}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome"
              required
              value={form.NOME_PESSOA_FISICA}
              onChange={(e) => updateField("NOME_PESSOA_FISICA", e.target.value)}
              disabled={saving}
            />
            <Input
              label="CPF"
              required
              inputMode="numeric"
              autoComplete="off"
              value={maskCpf(form.CPF_PESSOA_FISICA)}
              onChange={(e) =>
                updateField("CPF_PESSOA_FISICA", cpfDigitsOnly(e.target.value))
              }
              disabled={saving}
            />
            <Input
              label="RG"
              value={form.RG_PESSOA_FISICA}
              onChange={(e) => updateField("RG_PESSOA_FISICA", e.target.value)}
              disabled={saving}
            />
            <Input
              label="Nacionalidade"
              value={form.NACIONALIDADE_PESSOA_FISICA}
              onChange={(e) =>
                updateField("NACIONALIDADE_PESSOA_FISICA", e.target.value)
              }
              disabled={saving}
            />
            <Input
              label="Profissão"
              value={form.PROFISSAO_PESSOA_FISICA}
              onChange={(e) => updateField("PROFISSAO_PESSOA_FISICA", e.target.value)}
              disabled={saving}
            />
            <Select
              label="Estado civil"
              required
              placeholder="Selecione"
              options={estadoCivilPessoaFisicaOptions}
              value={form.ESTADO_CIVIL_PESSOA_FISICA}
              onChange={handleEstadoCivilChange}
              disabled={saving}
            />
          </div>

          {showConjuge && (
            <div className="border border-primary/10 rounded-lg p-4 space-y-4 bg-nested">
              <p className="text-sm font-medium text-foreground">Cônjuge</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nome do cônjuge"
                  required
                  value={form.conjuge.NOME_CONJUGE}
                  onChange={(e) => updateConjuge("NOME_CONJUGE", e.target.value)}
                  disabled={saving}
                />
                <Input
                  label="CPF do cônjuge"
                  inputMode="numeric"
                  autoComplete="off"
                  value={maskCpf(form.conjuge.CPF_CONJUGE)}
                  onChange={(e) =>
                    updateConjuge("CPF_CONJUGE", cpfDigitsOnly(e.target.value))
                  }
                  disabled={saving}
                />
                <Input
                  label="RG do cônjuge"
                  value={form.conjuge.RG_CONJUGE}
                  onChange={(e) => updateConjuge("RG_CONJUGE", e.target.value)}
                  disabled={saving}
                />
                <Input
                  label="Nacionalidade do cônjuge"
                  value={form.conjuge.NACIONALIDADE_CONJUGE}
                  onChange={(e) =>
                    updateConjuge("NACIONALIDADE_CONJUGE", e.target.value)
                  }
                  disabled={saving}
                />
                <Input
                  label="Profissão do cônjuge"
                  className="md:col-span-2"
                  value={form.conjuge.PROFISSAO_CONJUGE}
                  onChange={(e) =>
                    updateConjuge("PROFISSAO_CONJUGE", e.target.value)
                  }
                  disabled={saving}
                />
                <Input
                  label="E-mail do cônjuge"
                  type="email"
                  className="md:col-span-2"
                  value={form.conjuge.EMAIL_CONJUGE}
                  onChange={(e) =>
                    updateConjuge("EMAIL_CONJUGE", e.target.value)
                  }
                  disabled={saving}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Rua"
              value={form.RUA_ENDERECO_PESSOA_FISICA}
              onChange={(e) =>
                updateField("RUA_ENDERECO_PESSOA_FISICA", e.target.value)
              }
              disabled={saving}
            />
            <Input
              label="Número"
              value={form.N_ENDERECO_PESSOA_FISICA}
              onChange={(e) =>
                updateField("N_ENDERECO_PESSOA_FISICA", e.target.value)
              }
              disabled={saving}
            />
            <Input
              label="Bairro"
              value={form.BAIRRO_ENDERECO_PESSOA_FISICA}
              onChange={(e) =>
                updateField("BAIRRO_ENDERECO_PESSOA_FISICA", e.target.value)
              }
              disabled={saving}
            />
            <Input
              label="Município"
              value={form.MUN_ENDERECO_PESSOA_FISICA}
              onChange={(e) =>
                updateField("MUN_ENDERECO_PESSOA_FISICA", e.target.value)
              }
              disabled={saving}
            />
            <Input
              label="CEP"
              value={form.CEP_PESSOA_FISICA}
              onChange={(e) => updateField("CEP_PESSOA_FISICA", e.target.value)}
              disabled={saving}
            />
            <Input
              type="email"
              label="E-mail"
              value={form.EMAIL_PESSOA_FISICA}
              onChange={(e) =>
                updateField("EMAIL_PESSOA_FISICA", e.target.value)
              }
              disabled={saving}
            />
            <Input
              label="Telefone"
              className="md:col-span-2"
              inputMode="numeric"
              autoComplete="off"
              value={maskPhoneBr(form.TEL_PESSOA_FISICA)}
              onChange={(e) =>
                updateField("TEL_PESSOA_FISICA", phoneDigitsOnly(e.target.value))
              }
              disabled={saving}
            />
          </div>
        </div>

        <div className={MODAL_STICKY_FOOTER_END}>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
};

export default CustomerFormModal;
